import React, { useRef, useEffect, useState } from 'react';
import { Alert } from 'antd';
import { BulbFilled, BulbOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { prepareZXingModule, readBarcodes, ReaderOptions, ZXingModuleOverrides } from 'zxing-wasm/reader';
import zxingReaderWasm from 'zxing-wasm/reader/zxing_reader.wasm';
import { TooltipButton } from 'components/TooltipButton/TooltipButton';
import { Global } from 'constants/index';

import './styles.less';

type ScannerProps = {
  callback: (result: string) => void,
}

type TorchCapabilities = MediaTrackCapabilities & { torch?: boolean };
type TorchConstraints = MediaTrackConstraintSet & { torch?: boolean };
type TorchSupport = MediaTrackSupportedConstraints & { torch?: boolean };
// TS 4.9 does not know InputDeviceInfo.getCapabilities().
type CameraInfo = MediaDeviceInfo & { getCapabilities?: () => MediaTrackCapabilities };

// The decoder is served from our own build instead of the library's default CDN.
const ZXING_OVERRIDES: ZXingModuleOverrides = {
  locateFile: (path, prefix) => (path.endsWith('.wasm') ? zxingReaderWasm : prefix + path),
};
const READER_OPTIONS: ReaderOptions = { maxNumberOfSymbols: 1, textMode: 'Plain' };
const CAMERA_RESOLUTION = { width: { ideal: 1280 }, height: { ideal: 720 } };
// Some Android devices report the torch capability only after the stream has run for a moment.
const TORCH_WAIT_MS = 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const reportsTorch = async (track: MediaStreamTrack) => {
  for (let waited = 0; waited < TORCH_WAIT_MS; waited += 100) {
    const capabilities: TorchCapabilities | undefined = track.getCapabilities?.();
    if (capabilities?.torch) return true;
    await wait(100);
  }
  return false;
};

// Cameras that say they face the user are skipped without opening them.
const otherCameras = async (deviceId: string) => (await navigator.mediaDevices.enumerateDevices())
  .filter((device: CameraInfo) => device.kind === 'videoinput' && device.deviceId !== deviceId
    && !device.getCapabilities?.().facingMode?.includes('user'));

const Scanner = ({ callback }: ScannerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<MediaStreamTrack>();
  const [ data, setData ] = useState<string>();
  const [ error, setError ] = useState<string>();
  const [ hasTorch, setHasTorch ] = useState(false);
  const [ torchOn, setTorchOn ] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const video = videoRef.current;
    const context = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    if (!video || !context) return;

    let stream: MediaStream | undefined;
    let frame = 0;
    let stopped = false;

    const stopStream = () => stream?.getTracks().forEach((track) => track.stop());

    const stop = () => {
      stopped = true;
      cancelAnimationFrame(frame);
      stopStream();
    };

    const fail = (message: string) => {
      if (stopped) return;
      stop();
      setError(message);
    };

    const scan = () => {
      const { canvas } = context;
      if (!video.videoWidth) {
        frame = requestAnimationFrame(scan);
        return;
      }
      if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      }
      context.drawImage(video, 0, 0);
      readBarcodes(context.getImageData(0, 0, canvas.width, canvas.height), READER_OPTIONS)
        .then(([ result ]) => {
          if (stopped) return;
          if (!result) {
            frame = requestAnimationFrame(scan);
            return;
          }
          stop();
          setData(result.text);
        })
        .catch(() => fail('components.scanner.failed'));
    };

    const openCamera = async (camera: MediaTrackConstraints) =>
      navigator.mediaDevices.getUserMedia({ video: { ...camera, ...CAMERA_RESOLUTION } });

    const openDefaultBackCamera = () => openCamera({ facingMode: { exact: 'environment' } })
      .catch(() => openCamera({ facingMode: 'environment' }));

    // Phones often cannot run two cameras at once, so the previous one is stopped first.
    const showCamera = async (open: () => Promise<MediaStream>) => {
      stopStream();
      stream = await open();
      if (stopped) {
        stopStream();
        return undefined;
      }
      video.srcObject = stream;
      await video.play();
      const [ track ] = stream.getVideoTracks();
      trackRef.current = track;
      return track;
    };

    const startCamera = async () => {
      const rememberedCamera = localStorage.getItem(Global.SCANNER_CAMERA_KEY);
      let track = await showCamera(() => (rememberedCamera
        ? openCamera({ deviceId: { exact: rememberedCamera } }).catch(openDefaultBackCamera)
        : openDefaultBackCamera()));
      if (!track) return;
      scan();

      const { deviceId, facingMode } = track.getSettings();
      if (await reportsTorch(track)) return setHasTorch(true);

      // The default back camera may have no torch (a Galaxy S23 opens its ultra-wide), so the other back cameras
      // are tried once per device and the outcome is remembered.
      const torchSupport: TorchSupport = navigator.mediaDevices.getSupportedConstraints();
      if (stopped || !deviceId || deviceId === rememberedCamera || facingMode !== 'environment' || !torchSupport.torch) return;

      for (const camera of await otherCameras(deviceId)) {
        track = await showCamera(() => openCamera({ deviceId: { exact: camera.deviceId } })).catch(() => undefined);
        if (stopped) return;
        if (track?.getSettings().facingMode === 'environment' && await reportsTorch(track)) {
          localStorage.setItem(Global.SCANNER_CAMERA_KEY, camera.deviceId);
          return setHasTorch(true);
        }
      }

      await showCamera(() => openCamera({ deviceId: { exact: deviceId } }).catch(openDefaultBackCamera));
      localStorage.setItem(Global.SCANNER_CAMERA_KEY, deviceId);
    };

    // Loads the decoder while the camera starts. Promise.resolve: TS 4.9 types this overload as void.
    Promise.resolve(prepareZXingModule({ overrides: ZXING_OVERRIDES, fireImmediately: true }))
      .catch(() => fail('components.scanner.failed'));

    startCamera().catch(() => fail('components.scanner.nocamera'));

    return stop;
  }, []);

  useEffect(() => {
    if (callback && data) callback(data);
  }, [ data, callback ]);

  const toggleTorch = () => {
    const constraints: TorchConstraints = { torch: !torchOn };
    trackRef.current?.applyConstraints({ advanced: [ constraints ] })
      .then(() => setTorchOn(!torchOn))
      .catch(() => setHasTorch(false));
  };

  if (error) return <Alert type="warning" showIcon message={t(error)} />;

  return (
    <div className="scanner">
      <video ref={videoRef} className="scanner-video" muted playsInline />
      {hasTorch && (
        <div className="scanner-torch">
          <TooltipButton
            minimized
            title={t('components.scanner.torch')}
            buttonProps={{
              shape: 'circle',
              size: 'large',
              type: torchOn ? 'primary' : 'default',
              icon: torchOn ? <BulbFilled /> : <BulbOutlined />,
              onClick: toggleTorch,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Scanner;
